import type { ChangeEvent, FormEvent, KeyboardEvent } from 'react';
import React from 'react';
import * as m from '../paraglide/messages.js';
import type { AgentModelOptionView } from './api.js';
import type { PendingChatAttachment } from './chatAttachments.js';
import type { StudioInspectSelection } from './previewInspect.js';
import { CHAT_ATTACHMENT_ACCEPT } from './chatAttachments.js';
import {
  composerHasContent,
  shouldRecallNewerUserMessage,
  shouldRecallOlderUserMessage,
  shouldSubmitComposerOnEnter,
} from './composerKeys.js';
import {
  CameraIcon,
  CloseIcon,
  CrosshairIcon,
  MicIcon,
  PaperclipIcon,
  SendIcon,
  StopIcon,
  TeachPathIcon,
} from './icons.js';
import { inspectSelectionLabel } from './previewInspect.js';

export type ChatComposerProps = {
  readonly draft: string;
  readonly agentBusy: boolean;
  /** Disables the composer without showing the stop-turn control. */
  readonly locked?: boolean;
  readonly recallingHistory: boolean;
  readonly pendingAttachments: readonly PendingChatAttachment[];
  readonly selectedComponents: readonly StudioInspectSelection[];
  readonly inspectEnabled: boolean;
  readonly dictationSupported: boolean;
  readonly dictationListening: boolean;
  readonly screenshotSupported?: boolean;
  /** When true, the camera targets the last selected component instead of the full preview. */
  readonly screenshotUsesSelection?: boolean;
  readonly agentModelId?: string;
  readonly agentModels?: readonly AgentModelOptionView[];
  readonly onAgentModelChange?: (modelId: string) => void;
  readonly onDraftChange: (value: string) => void;
  readonly onPickAttachments?: (files: readonly File[]) => void;
  readonly onRemoveAttachment?: (id: string) => void;
  readonly onRemoveSelection?: (index: number) => void;
  readonly onCaptureScreenshot?: () => void;
  readonly onToggleInspect: () => void;
  readonly onToggleDictation: () => void;
  readonly onSend: () => void;
  readonly onStopTurn: () => void;
  readonly onRecallOlder: () => void;
  readonly onRecallNewer: () => void;
  /** When set, show teach-path control in the composer tool bar. */
  readonly teachEnabled?: boolean;
  readonly teachStatus?: 'idle' | 'recording' | 'finalizing' | 'ready' | 'failed';
  readonly teachBusy?: boolean;
  readonly onTeachToggle?: () => void;
};

function capturePreviewButtonTitle(props: {
  readonly screenshotSupported?: boolean;
  readonly screenshotUsesSelection?: boolean;
}): string {
  if (props.screenshotSupported === false) {
    return m.capturePreviewUnavailable();
  }
  if (props.screenshotUsesSelection === true) {
    return m.captureSelectionHint();
  }
  return m.capturePreviewHint();
}

type ComposerKeyDownInput = {
  readonly event: KeyboardEvent<HTMLTextAreaElement>;
  readonly draft: string;
  readonly attachmentCount: number;
  readonly selectionCount: number;
  readonly agentBusy: boolean;
  readonly recallingHistory: boolean;
  readonly onSend: () => void;
  readonly onRecallOlder: () => void;
  readonly onRecallNewer: () => void;
};

function handleComposerKeyDown(input: ComposerKeyDownInput): void {
  const {
    event,
    draft,
    attachmentCount,
    selectionCount,
    agentBusy,
    recallingHistory,
    onSend,
    onRecallOlder,
    onRecallNewer,
  } = input;
  const historyKey = {
    key: event.key,
    shiftKey: event.shiftKey,
    isComposing: event.nativeEvent.isComposing,
    keyCode: event.keyCode,
    draft,
    recallingHistory,
    agentBusy,
  };
  if (shouldRecallOlderUserMessage(historyKey)) {
    event.preventDefault();
    onRecallOlder();
    return;
  }
  if (shouldRecallNewerUserMessage(historyKey)) {
    event.preventDefault();
    onRecallNewer();
    return;
  }
  if (
    !shouldSubmitComposerOnEnter({
      key: event.key,
      shiftKey: event.shiftKey,
      isComposing: event.nativeEvent.isComposing,
      keyCode: event.keyCode,
    })
  ) {
    return;
  }
  event.preventDefault();
  if (agentBusy || !composerHasContent(draft, attachmentCount, selectionCount)) {
    return;
  }
  onSend();
}

function filesFromAttachmentInput(event: ChangeEvent<HTMLInputElement>): readonly File[] {
  const selected = event.currentTarget.files;
  if (selected === null || selected.length === 0) {
    return [];
  }
  return [...selected];
}

function toolClassName(active: boolean, disabled: boolean): string {
  if (disabled) {
    return 'icon-button composer-tool is-disabled';
  }
  if (active) {
    return 'icon-button composer-tool is-active';
  }
  return 'icon-button composer-tool';
}

export function ChatComposer(props: ChatComposerProps): React.ReactElement {
  const interactionLocked = props.agentBusy || props.locked === true;
  const canSend =
    !interactionLocked &&
    composerHasContent(
      props.draft,
      props.pendingAttachments.length,
      props.selectedComponents.length
    );
  const attachClassName = interactionLocked
    ? 'icon-button composer-tool is-disabled'
    : 'icon-button composer-tool';
  const inspectLabel = props.inspectEnabled ? m.stopSelectingComponent() : m.selectComponent();
  const dictationLabel = props.dictationListening ? m.stopDictation() : m.dictateMessage();
  return (
    <form
      className="chat-form"
      onSubmit={(event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!canSend) {
          return;
        }
        props.onSend();
      }}
    >
      <label htmlFor="chat-message" className="visually-hidden">
        {m.messagePlaceholder()}
      </label>
      {props.pendingAttachments.length > 0 ? (
        <ul className="chat-pending-attachments">
          {props.pendingAttachments.map((attachment) => {
            const isImagePreview =
              attachment.kind === 'image' && typeof attachment.previewUrl === 'string';
            return (
              <li key={attachment.id} className={isImagePreview ? 'chat-pending-image' : undefined}>
                {isImagePreview ? (
                  <a
                    className="chat-pending-thumb-link"
                    href={attachment.previewUrl}
                    download={attachment.fileName}
                    title={m.downloadAttachment({ fileName: attachment.fileName })}
                  >
                    <img
                      className="chat-pending-thumb"
                      src={attachment.previewUrl}
                      alt={attachment.fileName}
                    />
                  </a>
                ) : (
                  <span className="chat-pending-name">{attachment.fileName}</span>
                )}
                <button
                  type="button"
                  className="chip-remove"
                  disabled={interactionLocked}
                  aria-label={m.removeAttachment({ fileName: attachment.fileName })}
                  title={m.removeAttachment({ fileName: attachment.fileName })}
                  onClick={() => {
                    props.onRemoveAttachment?.(attachment.id);
                  }}
                >
                  <CloseIcon />
                </button>
              </li>
            );
          })}
        </ul>
      ) : undefined}
      {props.selectedComponents.length > 0 ? (
        <ul className="chat-pending-attachments">
          {props.selectedComponents.map((selection, index) => {
            const label = inspectSelectionLabel(selection);
            return (
              <li key={`${selection.selector}-${String(index)}`}>
                <span className="chat-pending-name">{label}</span>
                <button
                  type="button"
                  className="chip-remove"
                  disabled={interactionLocked}
                  aria-label={m.removeSelection({ label })}
                  title={m.removeSelection({ label })}
                  onClick={() => {
                    props.onRemoveSelection?.(index);
                  }}
                >
                  <CloseIcon />
                </button>
              </li>
            );
          })}
        </ul>
      ) : undefined}
      {props.inspectEnabled ? (
        <p className="inspect-hint" role="status">
          {m.inspectHint()}
        </p>
      ) : undefined}
      <textarea
        id="chat-message"
        rows={1}
        placeholder={m.messagePlaceholder()}
        value={props.draft}
        disabled={interactionLocked}
        onChange={(event) => {
          props.onDraftChange(event.target.value);
        }}
        onKeyDown={(event: KeyboardEvent<HTMLTextAreaElement>) => {
          handleComposerKeyDown({
            event,
            draft: props.draft,
            attachmentCount: props.pendingAttachments.length,
            selectionCount: props.selectedComponents.length,
            agentBusy: interactionLocked,
            recallingHistory: props.recallingHistory,
            onSend: props.onSend,
            onRecallOlder: props.onRecallOlder,
            onRecallNewer: props.onRecallNewer,
          });
        }}
      />
      <input
        id="chat-attachments"
        className="chat-file-input"
        type="file"
        multiple
        accept={CHAT_ATTACHMENT_ACCEPT}
        disabled={interactionLocked}
        onChange={(event) => {
          const picker = event.currentTarget;
          const selected = filesFromAttachmentInput(event);
          if (selected.length === 0) {
            return;
          }
          props.onPickAttachments?.(selected);
          picker.value = '';
        }}
      />
      <div className="composer-toolbar">
        <div className="composer-tools">
          <label htmlFor="chat-attachments" className={attachClassName} title={m.attachFiles()}>
            <span className="visually-hidden">{m.attachFiles()}</span>
            <PaperclipIcon />
          </label>
          <button
            type="button"
            className={toolClassName(
              false,
              interactionLocked || props.screenshotSupported === false
            )}
            aria-label={m.capturePreview()}
            title={capturePreviewButtonTitle(props)}
            disabled={interactionLocked || props.screenshotSupported === false}
            onClick={() => {
              props.onCaptureScreenshot?.();
            }}
          >
            <CameraIcon />
          </button>
          <button
            type="button"
            className={toolClassName(props.inspectEnabled, interactionLocked)}
            aria-pressed={props.inspectEnabled}
            aria-label={inspectLabel}
            title={inspectLabel}
            disabled={interactionLocked}
            onClick={props.onToggleInspect}
          >
            <CrosshairIcon />
          </button>
          <button
            type="button"
            className={toolClassName(props.dictationListening, interactionLocked)}
            aria-pressed={props.dictationListening}
            aria-label={dictationLabel}
            title={props.dictationSupported ? dictationLabel : m.dictationUnsupported()}
            disabled={interactionLocked || !props.dictationSupported}
            onClick={props.onToggleDictation}
          >
            <MicIcon />
          </button>
          {props.teachEnabled === true ? (
            <button
              type="button"
              className={toolClassName(
                props.teachStatus === 'recording' || props.teachStatus === 'finalizing',
                props.teachBusy === true || props.agentBusy || props.teachStatus === 'finalizing'
              )}
              aria-pressed={props.teachStatus === 'recording' || props.teachStatus === 'finalizing'}
              aria-label={
                props.teachStatus === 'recording' || props.teachStatus === 'finalizing'
                  ? m.teachBrowserStop()
                  : m.teachBrowserShowPath()
              }
              title={
                props.teachStatus === 'recording' || props.teachStatus === 'finalizing'
                  ? m.teachBrowserStop()
                  : m.teachBrowserShowPath()
              }
              disabled={
                props.teachBusy === true || props.agentBusy || props.teachStatus === 'finalizing'
              }
              onClick={() => {
                props.onTeachToggle?.();
              }}
            >
              <TeachPathIcon />
            </button>
          ) : undefined}
        </div>
        <div className="composer-toolbar-end">
          {props.agentModels !== undefined &&
          props.agentModels.length > 0 &&
          props.agentModelId !== undefined ? (
            <label className="composer-model" title={m.agentModelHint()}>
              <span className="visually-hidden">{m.agentModel()}</span>
              <select
                className="composer-model-select"
                value={props.agentModelId}
                disabled={interactionLocked || props.agentModels.length < 2}
                aria-label={m.agentModel()}
                onChange={(event) => {
                  props.onAgentModelChange?.(event.target.value);
                }}
              >
                {props.agentModels.map((model) => (
                  <option key={model.id} value={model.id}>
                    {model.label}
                  </option>
                ))}
              </select>
            </label>
          ) : undefined}
          {props.agentBusy ? (
            <button
              type="button"
              className="composer-send"
              aria-label={m.stopTurn()}
              title={m.stopTurn()}
              onClick={props.onStopTurn}
            >
              <StopIcon />
            </button>
          ) : (
            <button
              type="submit"
              className="composer-send"
              disabled={!canSend}
              aria-label={m.sendMessage()}
              title={m.sendMessage()}
            >
              <SendIcon />
            </button>
          )}
        </div>
      </div>
    </form>
  );
}
